import * as React from 'react';
import cx from 'classnames';
import { ContentState } from 'draft-js';
import { Button, Input, Tooltip, Dropdown, Menu } from 'antd';
import {
  XmarkIcon,
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  PencilIcon,
  CheckIcon,
  ArrowsUpDownLeftRightIcon
} from '@revfluence/fresh-icons/regular/esm';

import { ImageEntityData } from '../StyleControls/types';
import { imageResizeStore } from '../utils/imageResizeStore';
import styles from './ImageBlock.module.scss';

interface IProps {
  contentState: ContentState;
  entityKey: string;
  blockKey: string;
  offsetKey: string;
  updateEntityData?: (newContentState: ContentState, changeType?: string) => void;
}

const ALIGNMENT_OPTIONS = [
  { key: 'left', icon: <AlignLeftIcon width={14} height={14} />, label: 'Left' },
  { key: 'center', icon: <AlignCenterIcon width={14} height={14} />, label: 'Center' },
  { key: 'right', icon: <AlignRightIcon width={14} height={14} />, label: 'Right' },
];

export const ImageBlock: React.FC<IProps> = React.memo((props) => {
  const { contentState, entityKey, updateEntityData } = props;
  const entity = contentState.getEntity(entityKey);
  const data: ImageEntityData = entity.getData();
  const { src, alt = '', width, height, alignment = 'left' } = data;

  const [isResizing, setIsResizing] = React.useState(false);
  const [isEditingAlt, setIsEditingAlt] = React.useState(false);
  const [altText, setAltText] = React.useState(alt);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0, width: 0, height: 0 });
  const [resizePreview, setResizePreview] = React.useState({ width: 0, height: 0 });
  const imageRef = React.useRef<HTMLImageElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const updateEntity = React.useCallback((newData: Partial<ImageEntityData>) => {
    if (!updateEntityData) return;

    const newContentState = contentState.mergeEntityData(entityKey, newData);

    updateEntityData(newContentState, 'apply-entity');
  }, [contentState, entityKey, updateEntityData]);

  const handleAlignmentChange = React.useCallback((newAlignment: string) => {
    updateEntity({ alignment: newAlignment as 'left' | 'center' | 'right' });
  }, [updateEntity]);

  const handleDelete = React.useCallback(() => {
    if (!updateEntityData) return;

    // Create a new content state without this entity
    const newContentState = contentState.mergeEntityData(entityKey, {});
    updateEntityData(newContentState);
  }, [contentState, entityKey, updateEntityData]);

  const handleAltTextSave = React.useCallback(() => {
    updateEntity({ alt: altText });
    setIsEditingAlt(false);
  }, [updateEntity, altText]);

  const handleMouseDown = React.useCallback((event: React.MouseEvent) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const target = event.target as HTMLElement;

    // Check if the target is the resize handle or any of its children
    const isResizeHandle = target.classList.contains(styles.resizeHandle) ||
                          target.closest(`.${styles.resizeHandle}`);

    if (isResizeHandle) {
      event.preventDefault();
      event.stopPropagation();

      const currentWidth = width || rect.width;
      const currentHeight = height || rect.height;

      setIsResizing(true);
      setDragStart({
        x: event.clientX,
        y: event.clientY,
        width: currentWidth,
        height: currentHeight,
      });
      setResizePreview({ width: currentWidth, height: currentHeight });
    }
  }, [width, height]);

  const handleMouseMove = React.useCallback((event: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = event.clientX - dragStart.x;

    // Maintain aspect ratio - resize from bottom-right corner
    const aspectRatio = dragStart.width / dragStart.height;
    const newWidth = Math.max(100, Math.min(800, dragStart.width + deltaX));
    const newHeight = Math.max(100, newWidth / aspectRatio);

    setResizePreview({ width: newWidth, height: newHeight });
  }, [isResizing, dragStart]);

  const handleMouseUp = React.useCallback(() => {
    if (isResizing) {
      // Apply the final resize
      const newWidth = Math.round(resizePreview.width);
      const newHeight = Math.round(resizePreview.height);

      // Store dimensions in global store for export
      imageResizeStore.setDimensions(src, newWidth, newHeight);

      // Verify the store has the data
      imageResizeStore.getDimensions(src);

      updateEntity({
        width: newWidth,
        height: newHeight
      });
      setIsResizing(false);
      setResizePreview({ width: 0, height: 0 });

      // Force a re-export by triggering a small change in editor state
      // This ensures the new dimensions are captured in the HTML output
      setTimeout(() => {
        // The updateEntity call above should trigger this automatically
      }, 100);
    }
  }, [isResizing, resizePreview, updateEntity, src]);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Get current dimensions for display
  const currentWidth = isResizing ? resizePreview.width : (width || 'auto');
  const currentHeight = isResizing ? resizePreview.height : (height || 'auto');

  const alignmentMenu = (
    <Menu>
      {ALIGNMENT_OPTIONS.map(({ key, icon, label }) => (
        <Menu.Item
          key={key}
          onClick={() => handleAlignmentChange(key)}
          className={cx({ [styles.activeAlignment]: alignment === key })}
        >
          <div className={styles.menuItem}>
            {icon}
            <span>{label}</span>
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <div
      ref={containerRef}
      className={cx(styles.ImageBlock, styles[`align-${alignment}`], {
        [styles.resizing]: isResizing
      })}
      onMouseDown={handleMouseDown}
    >
      <div className={styles.imageContainer}>
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          style={{
            width: typeof currentWidth === 'number' ? `${currentWidth}px` : currentWidth,
            height: typeof currentHeight === 'number' ? `${currentHeight}px` : currentHeight,
          }}
          className={styles.image}
          draggable={false}
        />

        {/* Resize overlay during resizing */}
        {isResizing && (
          <div className={styles.resizeOverlay}>
            <div className={styles.resizeInfo}>
              {typeof currentWidth === 'number' ? Math.round(currentWidth) : 'auto'} × {typeof currentHeight === 'number' ? Math.round(currentHeight) : 'auto'}
            </div>
          </div>
        )}

        {/* Controls overlay */}
        <div className={styles.controls}>
          {/* Alignment controls */}
          <Dropdown overlay={alignmentMenu} trigger={['click']} placement="topLeft">
            <Button size="small" className={styles.controlButton}>
              {ALIGNMENT_OPTIONS.find((opt) => opt.key === alignment)?.icon}
            </Button>
          </Dropdown>

          {/* Alt text editing */}
          {isEditingAlt ? (
            <div className={styles.altEditContainer}>
              <Input
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                placeholder="Alt text"
                size="small"
                className={styles.altInput}
                onPressEnter={handleAltTextSave}
                onBlur={handleAltTextSave}
                autoFocus
              />
              <Button
                size="small"
                type="primary"
                icon={<CheckIcon width={12} height={12} />}
                className={styles.altSaveButton}
                onClick={handleAltTextSave}
              />
            </div>
          ) : (
            <Tooltip title="Edit alt text">
              <Button
                size="small"
                className={styles.controlButton}
                onClick={() => setIsEditingAlt(true)}
              >
                <PencilIcon width={12} height={12} />
              </Button>
            </Tooltip>
          )}

          {/* Delete button */}
          <Tooltip title="Delete image">
            <Button
              size="small"
              danger
              className={styles.controlButton}
              onClick={handleDelete}
            >
              <XmarkIcon width={12} height={12} />
            </Button>
          </Tooltip>
        </div>

        {/* Single resize handle - bottom right corner */}
        <div className={cx(styles.resizeHandle, styles.bottomRight)}>
          <ArrowsUpDownLeftRightIcon width={16} height={16} />
        </div>
      </div>
    </div>
  );
});

ImageBlock.displayName = 'ImageBlock';
